/**
 * @file index.ts
 * @description 视频面板
 * @author xingwei
 * @createTime 2025-08-22
 * @lastEditTime 2025-08-22
 */

import _ from 'lodash'
import { Group, type SceneControl, Vector3, createLabel } from '@anov/3d-core'
import ObjectLayer from '../Base/ObjectLayer'
import type { IPanel, Option, TitleOption } from './type'

const DEFAULT_OPTION = {
  position: [0, 0, 0],
  width: 300,
  height: 200,
  bgColor: '#fff',
  borderRadius: 8,
  closeable: true,
  padding: '10px',
  volume: 0.5,
  src: '',
  autoplay: false,
  controls: false,
  loop: false,
  muted: false,
  poster: '',
  videoHeight: 150,
}

class VideoPanel extends ObjectLayer implements IPanel {
  private _title: TitleOption
  private _width: number
  private _height: number
  private _bgColor: string
  private _position: Array<number>
  private _borderRadius = 10
  private _bgUrl: string
  private _className: string
  private _customStyle: any
  private _closeable: boolean
  private _padding: string
  private _group: Group

  private _videoElement: HTMLVideoElement
  private _src: string
  private _autoplay: boolean
  private _controls: boolean
  private _loop: boolean
  private _muted: boolean
  private _poster?: string
  private _volume: number
  private _videoHeight: number
  private _videoWidth: number

  private _onClose: Function
  visible = false

  constructor(option: Option, sceneControl: SceneControl) {
    super(sceneControl)
    const params = _.mergeWith(_.cloneDeep(DEFAULT_OPTION), option)
    const {
      title: _title,
      width: _width,
      height: _height,
      bgColor: _bgColor,
      position: _position,
      bgUrl: _bgUrl,
      className: _className,
      customStyle: _customStyle,
      borderRadius: _borderRadius,
      padding: _padding,
      closeable: _closeable,
      autoplay: _autoplay,
      controls: _controls,
      loop: _loop,
      muted: _muted,
      poster: _poster,
      volume: _volume,
      src: _src,
      videoHeight: _videoHeight,
      videoWidth: _videoWidth,
      onClose: _onClose,
    } = params

    Object.assign(this, {
      _videoWidth,
      _videoHeight,
      _title,
      _autoplay,
      _loop,
      _muted,
      _volume,
      _poster,
      _controls,
      _src,
      _width,
      _height,
      _bgColor,
      _position,
      _bgUrl,
      _className,
      _customStyle,
      _borderRadius,
      _padding,
      _closeable,
      _onClose,
    })

    this._videoElement = this.createVideoDom()
    this._group = new Group()
    this._group.visible = this.visible
    this.init()
  }

  private init() {
    this.createBox()
  }

  private createBox() {
    // 使用 DocumentFragment 减少重排
    // const fragment = document.createDocumentFragment();
    const bgDom = this.createBgDom() // 创建容器盒子
    const videoContainer = document.createElement('div')

    if (this._title?.value || this._closeable) {
      const headerDom = this.createHeaderDom() // 创建头部盒子
      bgDom.appendChild(headerDom)
    }
    videoContainer.appendChild(this._videoElement)
    bgDom.appendChild(videoContainer)

    const bgObject = createLabel(bgDom)
    bgObject.position.set(0, 0, 0)
    bgObject.center.set(0.5, 0.5)

    this._group.add(bgObject)

    this.add(this._group)
    this.applyPosition() // 初始化自动定位
  }

  /** 创建背景 */
  private createBgDom() {
    // 创建容器盒子
    const bgDom = document.createElement('div')
    Object.assign(bgDom.style, {
      width: `${this._width}px`,
      height: `${this._height}px`,
      // left: `${this._position[0]}px`,
      // top: `${this._position[1]}px`,
      overflow: 'hidden',
      padding: this._padding,
      borderRadius: this._bgUrl ? '' : `${this._borderRadius}px`,
      backgroundColor: this._bgUrl ? '' : this._bgColor,
      backgroundImage: this._bgUrl ? `url(${this._bgUrl})` : '',
      backgroundSize: this._bgUrl ? 'contain' : '',
      backgroundRepeat: this._bgUrl ? 'no-repeat' : '',
      boxSizing: 'border-box',
      display: 'flex',

    })

    if (this._className)
      bgDom.className = this._className
    if (this._customStyle)
      Object.assign(bgDom.style, this._customStyle)
    return bgDom
  }

  /** 创建头部 */
  private createHeaderDom() {
    const headerDom = document.createElement('div')
    headerDom.className = 'video-panel-header'
    headerDom.style.display = 'flex'
    headerDom.style.alignItems = 'center'
    headerDom.style.justifyContent = 'flex-end'
    if (this._title?.value) {
      // 创建标题
      const titleDom = document.createElement('p')
      titleDom.className = 'panel-title'
      titleDom.style.display = 'flex'
      titleDom.style.flex = '1'
      titleDom.textContent = this._title.value
      const { value: titleValue, ...titleStyle } = this._title
      // 正确设置样式的方式
      Object.assign(titleDom.style, titleStyle) // 待测试

      headerDom.appendChild(titleDom)
    }

    // 创建关闭按钮
    if (this._closeable) {
      const closeDom = document.createElement('div')
      closeDom.className = 'panel-close'
      closeDom.style.cursor = 'pointer'
      closeDom.style.fontSize = this._title?.fontSize
      closeDom.style.color = this._title?.color
      closeDom.textContent = 'x'
      closeDom.style.pointerEvents = 'auto'
      closeDom.onclick = () => {
        this.close()
      }

      headerDom.appendChild(closeDom)
    }
    return headerDom
  }

  private createVideoDom() {
    const videoDom = document.createElement('video')
    videoDom.style.width = this._videoWidth ? `${this._videoWidth}px` : '100%'
    videoDom.style.height = this._videoHeight ? `${this._videoHeight}px` : '100%'
    videoDom.style.objectFit = 'cover'
    videoDom.src = this._src
    videoDom.autoplay = this._autoplay
    videoDom.controls = this._controls
    videoDom.loop = this._loop
    videoDom.muted = this._muted
    videoDom.volume = this._volume

    if (this._poster)
      videoDom.poster = this._poster
    return videoDom
  }

  /** 自动定位面板 */
  private applyPosition(offset: Vector3 = new Vector3(0, 0, 0)) {
    if (Array.isArray(this._position)) {
      // 如果是数组 [x, y]
      const [x, y, z] = this._position
      this._group.position.set(x, -y, z) // 注意y轴取反
    }
  }

  public open() {
    this.visible = true
    this._group.visible = true

    // 同步 DOM 节点显示
    // this._group.children.forEach((child: any) => {
    //     if (child.element) {
    //         child.element.style.display = 'block';
    //     }
    // });
  }

  public close() {
    this.visible = false
    this._group.visible = false

    // 同步 DOM 节点隐藏
    // this._group.children.forEach((child: any) => {
    //     if (child.element) {
    //         child.element.style.display = 'none';
    //     }
    // });
    if (this._onClose)
      this._onClose()
  }

  public update(option: Option) {
    if (option.src) {
      this._src = option.src
      this._videoElement.src = option.src
    }
    if (option.autoplay !== undefined)
      this._autoplay = option.autoplay
    if (option.controls !== undefined)
      this._videoElement.controls = option.controls
    if (option.loop !== undefined)
      this._videoElement.loop = option.loop
    if (option.muted !== undefined)
      this._videoElement.muted = option.muted
    if (option.volume !== undefined)
      this._videoElement.volume = option.volume
    if (option.poster)
      this._videoElement.poster = option.poster
  }

  // 视频控制方法
  public play(): void {
    this._videoElement.play()
  }

  public pause(): void {
    this._videoElement.pause()
  }

  public setVolume(volume: number): void {
    this._videoElement.volume = Math.max(0, Math.min(1, volume))
  }

  public seek(time: number): void {
    this._videoElement.currentTime = time
  }

  public toggleMute(): void {
    this._videoElement.muted = !this._videoElement.muted
  }

  public get duration(): number {
    return this._videoElement.duration
  }

  public get currentTime(): number {
    return this._videoElement.currentTime
  }

  public get isPlaying(): boolean {
    return !this._videoElement.paused && !this._videoElement.ended
  }

  destroy(): void {
    this._group.clear()
    this.remove(this._group)
    // 清理DOM引用
    this._group.children.forEach((child: any) => {
      if (child.element && child.element.parentNode)
        child.element.parentNode.removeChild(child.element)
    })
    this._videoElement.pause()
    this._videoElement.src = ''
    this._videoElement.load() // 清理视频资源
  }
}

export { VideoPanel }